export const DEFAULT_REST_ROUTE_OPTIONS = [
  'Rest-Pasta',
  'Rest-Ris',
  'Rest-Kartofler',
  'Rest-Tomatbase',
  'Rest-Bælgfrugter',
  'Rest-Grønt',
  'Rest-Mejeri',
  'Rest-Æg',
  'Rest-Tilberedt kød',
  'Rest-Tilberedt kylling'
];

export const RECIPE_IMAGE_POSITIONS = {
  R021: '50% 66%',
  R046: '50% 74%',
};

function normalizeImagePath(path) {
  const value = normalize(path);
  if (!value) {
    return '';
  }
  if (/^(https?:|data:|\/)/i.test(value)) {
    return value;
  }
  if (value.startsWith('images/')) {
    return `/${value}`;
  }
  if (/\.(png|jpg|jpeg|webp|avif)$/i.test(value)) {
    return `/images/recipes/${value}`;
  }
  return `/images/recipes/${value}.png`;
}

function buildTitleBasedImageName(title) {
  const rawTitle = normalize(title);
  if (!rawTitle) {
    return '';
  }

  return rawTitle
    .replace(/[.,!?;:'"()]/g, '')
    .replace(/[\\/]/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function getRecipeImageCandidates(recipe, fallbackTitle = '') {
  const candidates = [];
  const addCandidate = (path) => {
    const resolved = normalizeImagePath(path);
    if (resolved && !candidates.includes(resolved)) {
      candidates.push(resolved);
    }
  };

  addCandidate(recipe?.image);
  addCandidate(recipe?.image_path);

  const recipeId = normalize(recipe?.recipe_id);
  if (recipeId) {
    addCandidate(`${recipeId}.png`);
  }

  const title = normalize(recipe?.name) || normalize(recipe?.title) || normalize(fallbackTitle);
  const titleBasedName = buildTitleBasedImageName(title);
  if (titleBasedName) {
    addCandidate(`${titleBasedName}.png`);
  }

  return candidates;
}

export function getRecipeImagePosition(recipeId) {
  return RECIPE_IMAGE_POSITIONS[normalize(recipeId)] || 'center';
}

export function normalize(value) {
  return (value || '').toString().trim();
}

export function normalizeSection(value) {
  return normalize(value).toLowerCase();
}

export function parseRestRoute(restTip) {
  const tip = normalize(restTip);
  if (!tip) {
    return '';
  }

  const exact = DEFAULT_REST_ROUTE_OPTIONS.find((route) => tip.includes(route));
  if (exact) {
    return exact;
  }

  const match = tip.match(/Rest-[A-Za-zÆØÅæøå]+(?: [A-Za-zÆØÅæøå]+)*/u);
  return match ? normalize(match[0]) : '';
}

export function getRestRouteOptions(recipes) {
  const discoveredRoutes = (Array.isArray(recipes) ? recipes : [])
    .map((recipe) => parseRestRoute(recipe.rest_tip))
    .filter(Boolean);

  const uniqueRoutes = Array.from(
    new Set([...DEFAULT_REST_ROUTE_OPTIONS, ...discoveredRoutes])
  );

  return uniqueRoutes.sort((a, b) => a.localeCompare(b, 'da-DK'));
}

export function formatTimeRange(tidMin, tidMax) {
  const min = normalize(tidMin);
  const max = normalize(tidMax);
  const minValue = Number(min);
  const maxValue = Number(max);

  if (min && max) {
    if (!Number.isNaN(minValue) && !Number.isNaN(maxValue)) {
      if (minValue === maxValue) {
        return `${maxValue} min`;
      }
      if (maxValue > minValue) {
        return `ca. ${maxValue} min`;
      }
    }
    return `${max} min`;
  }
  if (min) {
    return `${min} min`;
  }
  if (max) {
    return `${max} min`;
  }
  return '';
}

export function formatPackageSize(ingredient) {
  const amount = normalize(ingredient.pakke_stoerrelse);
  const unit = normalize(ingredient.pakke_enhed);
  const packs = normalize(ingredient.antal_pakker);
  const productName = normalize(ingredient.produkt_navn).toLocaleLowerCase('da-DK');

  const amountWithUnit = [amount, unit].filter(Boolean).join(' ');
  if (packs && amountWithUnit) {
    if (unit.toLocaleLowerCase('da-DK') === 'stk') {
      if (productName.includes('æg')) {
        return `${packs} bakke á ${amount} æg`;
      }
      return `${packs} pakke á ${amount} stk`;
    }
    return `${packs} pakke á ${amountWithUnit}`;
  }
  return amountWithUnit || packs;
}

export function formatUsage(ingredient) {
  const amount = normalize(ingredient.brug_maengde);
  const unit = normalize(ingredient.brug_enhed);
  const useWholePackage = normalize(ingredient.brug_er_hele_pakken).toLowerCase();

  const structuredUsage = [amount, unit].filter(Boolean).join(' ').trim();
  if (structuredUsage) {
    return structuredUsage;
  }
  if (useWholePackage === 'true' || useWholePackage === 'ja' || useWholePackage === '1') {
    return 'Hele pakken';
  }
  return '';
}

export function formatShoppingMeta(ingredient) {
  const packageSize = formatPackageSize(ingredient);
  const usage = formatUsage(ingredient);

  if (usage && packageSize) {
    if (usage === 'Hele pakken') {
      return `Hele pakken (${packageSize})`;
    }
    return `${usage} (${packageSize})`;
  }
  return usage || packageSize;
}

export function formatOptionalIngredientNote(ingredient) {
  const note = normalize(ingredient.note);
  if (!note) {
    return 'Brug efter smag.';
  }

  const lower = note.toLocaleLowerCase('da-DK');
  const hasVerbLead = /^(brug|rør|kom|vend|drys|server|top|smag|tilsæt|pres|bag|kog|bland|steg)\b/.test(lower);
  const hasQuantityLead = /^(\d+|en\b|et\b|lidt\b|efter\b)/.test(lower);
  const formatted = hasVerbLead ? note : hasQuantityLead ? `Brug ${note}` : note;

  return /[.!?]$/.test(formatted) ? formatted : `${formatted}.`;
}

export function formatIngredientLabel(value) {
  const text = normalize(value);
  if (!text) {
    return '';
  }

  return text
    .toLocaleLowerCase('da-DK')
    .split(/(\s+|-|\/|\(|\))/)
    .map((part) => {
      if (!part || /^(\s+|-|\/|\(|\))$/.test(part)) {
        return part;
      }

      const chars = Array.from(part);
      if (chars.length === 0) {
        return part;
      }

      return `${chars[0].toLocaleUpperCase('da-DK')}${chars.slice(1).join('')}`;
    })
    .join('');
}
